import { EventEmitter } from './event-emitter';
import { Token } from './token';

type UserEvent = 'user-signin' | 'user-signout' | 'user-loaded';

type AddressClaim = {
  formatted?: string;
  street_address?: string;
  locality?: string;
  region?: string;
  postal_code?: string;
  country?: string;
};

type StandardClaims = {
  sub: string;
  name: string;
  given_name: string;
  family_name: string;
  middle_name: string;
  nickname: string;
  preferred_username: string;
  profile: string; // URL to profile page
  picture: string; // URL to profile picture
  website: string; // URL to website
  email: string;
  email_verified: boolean;
  gender: string;
  birthdate: string; // YYYY-MM-DD. The year may be 0000, indicating it is omitted.
  zoneinfo: string; // Time zone, e.g. America/Los_Angeles, Europe/Paris
  locale: string; // Locale, e.g. en-US, fr-FR
  phone_number: string; // Example: +1 (425) 555-1212
  phone_number_verified: boolean;
  address: AddressClaim;
  updated_at: number; // Time the user's information was last updated. Its value is a JSON number representing the number of seconds from 1970-01-01T0:0:0Z as measured in UTC until the date/time.
};

export type User = {
  claims?: StandardClaims;
};

export class AuthManager {
  private static instance: AuthManager;

  private events: EventEmitter<UserEvent> = new EventEmitter([
    'user-signin',
    'user-signout',
    'user-loaded',
  ]);
  private user: User = { claims: undefined };
  private token: Token;

  private constructor(token: Token, claims?: StandardClaims) {
    this.token = token;
    if (claims) {
      this.user = { claims };
    }
  }

  static getInstance(token: Token): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager(token);
    }

    return AuthManager.instance;
  }

  /**
   * URL to sign in to the application.
   */
  static signInURL = '/oauth/signin';

  /**
   * URL to sign up to the application.
   */
  static signUpURL = '/oauth/signup';

  /**
   * URL to sign out of the application.
   */
  static signOutURL = '/oauth/signout';

  async getUser() {
    if (!this.user) {
      this.user = await fetchUser(this.token);
    }

    this.events.emit('user-loaded', this.user);

    return this.user;
  }
}

async function fetchUser(token: Token): Promise<User> {
  const authorization = token.authorizationHeaderValue;

  if (!authorization) {
    throw new Error('Unable to fetch user info - no authorization token');
  }

  const response = await fetch(
    'https://id.stage.housinganywhere.com/userinfo',
    {
      headers: {
        authorization,
      },
    },
  );

  if (!response.ok) {
    throw new Error('Unable to fetch user info');
  }

  const claims = await response.json();

  return { claims };
}
